import type { Plant } from '../types'

export const plants: Plant[] = [
  // ─── SPILLERS ─────────────────────────────────────────────────────────────
  {
    id: 'sweet-potato-vine',
    name: 'Sweet Potato Vine',
    scientificName: 'Ipomoea batatas',
    emoji: '🍃',
    accentColor: '#a8d5a2',
    role: ['spiller'],
    hardiness: { min: 9, max: 13 },
    sunlight: ['full-sun', 'partial-shade'],
    water: 'moderate',
    minContainerSize: 'medium',
    colors: ['lime', 'purple', 'bronze'],
    bloomMonths: [],
    pestResistance: 'moderate',
    nativeRegions: ['SE'],
    toxicity: { pets: true, children: false },
    windTolerance: 'moderate',
    isPerennial: false,
    overwinteringNote: 'Annual in most zones. Bring tubers indoors before frost.',
    wateringFrequency: 'Every 1–2 days in summer heat',
    fertilizingFrequency: 'Every 2 weeks with balanced liquid fertilizer',
    description: 'Dramatic cascading foliage in lime green, purple, or bronze. Grows fast and fills containers beautifully. Mostly grown for foliage, not flowers.',
    careLevel: 'easy',
    height: 'low',
    tags: ['foliage', 'fast-growing', 'colorful'],
  },
  {
    id: 'million-bells',
    name: 'Million Bells',
    scientificName: 'Calibrachoa × hybrida',
    emoji: '🌸',
    accentColor: '#f4a0b0',
    role: ['spiller', 'filler'],
    hardiness: { min: 9, max: 13 },
    sunlight: ['full-sun', 'partial-shade'],
    water: 'moderate',
    minContainerSize: 'small',
    colors: ['pink', 'purple', 'yellow', 'red', 'white', 'orange'],
    bloomMonths: [4, 5, 6, 7, 8, 9, 10],
    pestResistance: 'moderate',
    nativeRegions: [],
    toxicity: { pets: false, children: false },
    windTolerance: 'moderate',
    isPerennial: false,
    overwinteringNote: 'Annual. Self-cleaning — no deadheading needed.',
    wateringFrequency: 'Every 1–2 days; sensitive to drought',
    fertilizingFrequency: 'Weekly with bloom fertilizer for best results',
    description: 'Tiny petunia-like blooms that absolutely smother the plant all season. One of the best spillers for containers. Comes in nearly every color.',
    careLevel: 'easy',
    height: 'low',
    tags: ['long-blooming', 'no-deadheading', 'colorful'],
  },
  {
    id: 'bacopa',
    name: 'Bacopa',
    scientificName: 'Sutera cordata',
    emoji: '🤍',
    accentColor: '#dce8f5',
    role: ['spiller'],
    hardiness: { min: 9, max: 11 },
    sunlight: ['full-sun', 'partial-shade'],
    water: 'moderate',
    minContainerSize: 'small',
    colors: ['white', 'pink', 'lavender'],
    bloomMonths: [4, 5, 6, 7, 8, 9, 10],
    pestResistance: 'high',
    nativeRegions: [],
    toxicity: { pets: false, children: false },
    windTolerance: 'low',
    isPerennial: false,
    overwinteringNote: 'Annual in most climates. Prefers cooler summers.',
    wateringFrequency: 'Keep evenly moist; wilts quickly in heat',
    fertilizingFrequency: 'Every 2 weeks',
    description: 'Delicate trailing stems covered in tiny star-shaped flowers. Perfect for softening container edges. Does best in mild climates — may slow in intense heat.',
    careLevel: 'easy',
    height: 'low',
    tags: ['delicate', 'trailing', 'cottage-style'],
  },
  {
    id: 'lobelia',
    name: 'Lobelia',
    scientificName: 'Lobelia erinus',
    emoji: '💙',
    accentColor: '#90d5f0',
    role: ['spiller', 'filler'],
    hardiness: { min: 2, max: 11 },
    sunlight: ['full-sun', 'partial-shade'],
    water: 'moderate',
    minContainerSize: 'small',
    colors: ['blue', 'purple', 'white', 'pink'],
    bloomMonths: [4, 5, 6, 7, 8, 9],
    pestResistance: 'high',
    nativeRegions: [],
    toxicity: { pets: true, children: true },
    windTolerance: 'moderate',
    isPerennial: false,
    overwinteringNote: 'Annual. Shear back in midsummer for a second flush of blooms.',
    wateringFrequency: 'Every 1–2 days; keep moist',
    fertilizingFrequency: 'Every 2–3 weeks',
    description: 'Masses of tiny bright blue or purple flowers — one of the few true-blue annuals. Cascades beautifully over pot edges. Toxic if eaten, so keep away from pets.',
    careLevel: 'easy',
    height: 'low',
    tags: ['blue-flowers', 'cool-season', 'toxic'],
  },
  {
    id: 'creeping-jenny',
    name: 'Creeping Jenny',
    scientificName: 'Lysimachia nummularia',
    emoji: '🌿',
    accentColor: '#c8e6c9',
    role: ['spiller'],
    hardiness: { min: 4, max: 9 },
    sunlight: ['full-sun', 'partial-shade', 'full-shade'],
    water: 'high',
    minContainerSize: 'small',
    colors: ['chartreuse', 'green'],
    bloomMonths: [6, 7],
    pestResistance: 'high',
    nativeRegions: ['NE', 'MW', 'SE'],
    toxicity: { pets: false, children: false },
    windTolerance: 'moderate',
    isPerennial: true,
    overwinteringNote: 'Perennial in zones 4–9. May die back in winter but returns in spring.',
    wateringFrequency: 'Keep consistently moist',
    fertilizingFrequency: 'Monthly',
    description: 'Brilliant chartreuse trailing foliage that glows in the garden. One of the few spillers that tolerates shade. Tiny yellow flowers in summer are a bonus.',
    careLevel: 'easy',
    height: 'low',
    tags: ['shade-tolerant', 'foliage', 'perennial'],
  },
  {
    id: 'dichondra',
    name: 'Silver Falls Dichondra',
    scientificName: 'Dichondra argentea',
    emoji: '🩶',
    accentColor: '#c8d8e0',
    role: ['spiller'],
    hardiness: { min: 10, max: 13 },
    sunlight: ['full-sun', 'partial-shade'],
    water: 'low',
    minContainerSize: 'small',
    colors: ['silver', 'grey'],
    bloomMonths: [],
    pestResistance: 'high',
    nativeRegions: ['SW'],
    toxicity: { pets: false, children: false },
    windTolerance: 'high',
    isPerennial: true,
    overwinteringNote: 'Perennial in zones 10+. Annual elsewhere. Handles heat and drought exceptionally well.',
    wateringFrequency: 'Every 3–4 days; very drought-tolerant',
    fertilizingFrequency: 'Monthly',
    description: 'Stunning silver cascading foliage that can trail 2–4 feet. Adds a cool, metallic contrast to brighter companions. Exceptional heat and drought tolerance.',
    careLevel: 'easy',
    height: 'low',
    tags: ['silver-foliage', 'drought-tolerant', 'heat-tolerant'],
  },
  {
    id: 'ivy-geranium',
    name: 'Ivy Geranium',
    scientificName: 'Pelargonium peltatum',
    emoji: '🌺',
    accentColor: '#f9a0c0',
    role: ['spiller', 'filler'],
    hardiness: { min: 10, max: 13 },
    sunlight: ['full-sun', 'partial-shade'],
    water: 'moderate',
    minContainerSize: 'medium',
    colors: ['pink', 'red', 'white', 'purple', 'salmon'],
    bloomMonths: [4, 5, 6, 7, 8, 9, 10],
    pestResistance: 'moderate',
    nativeRegions: [],
    toxicity: { pets: true, children: false },
    windTolerance: 'moderate',
    isPerennial: true,
    overwinteringNote: 'Perennial in zones 10+. Bring indoors before frost elsewhere.',
    wateringFrequency: 'Every 2–3 days; allow top inch to dry',
    fertilizingFrequency: 'Every 2 weeks with bloom fertilizer',
    description: 'Lush trailing geranium with glossy leaves and clusters of blooms. More heat-tolerant than upright geraniums. A classic for window boxes and hanging baskets.',
    careLevel: 'easy',
    height: 'low',
    tags: ['heat-tolerant', 'window-box', 'classic'],
  },

  // ─── FILLERS ──────────────────────────────────────────────────────────────
  {
    id: 'petunia',
    name: 'Petunia',
    scientificName: 'Petunia × atkinsiana',
    emoji: '🌸',
    accentColor: '#c3a8e8',
    role: ['filler'],
    hardiness: { min: 10, max: 13 },
    sunlight: ['full-sun'],
    water: 'moderate',
    minContainerSize: 'small',
    colors: ['purple', 'pink', 'red', 'white', 'yellow', 'striped'],
    bloomMonths: [4, 5, 6, 7, 8, 9, 10],
    pestResistance: 'moderate',
    nativeRegions: [],
    toxicity: { pets: false, children: false },
    windTolerance: 'moderate',
    isPerennial: false,
    overwinteringNote: 'Annual. Deadhead regularly for continuous blooms.',
    wateringFrequency: 'Every 1–2 days; sensitive to drought',
    fertilizingFrequency: 'Weekly with bloom booster for maximum flowering',
    description: 'The quintessential container flower. Endless color options, long bloom season, and incredible value. Pinch back leggy stems in midsummer to keep bushy.',
    careLevel: 'easy',
    height: 'low',
    tags: ['classic', 'long-blooming', 'deadhead'],
  },
  {
    id: 'begonia-wax',
    name: 'Wax Begonia',
    scientificName: 'Begonia × semperflorens',
    emoji: '🌺',
    accentColor: '#f9c0b0',
    role: ['filler'],
    hardiness: { min: 10, max: 13 },
    sunlight: ['partial-shade', 'full-shade'],
    water: 'moderate',
    minContainerSize: 'small',
    colors: ['red', 'pink', 'white'],
    bloomMonths: [5, 6, 7, 8, 9, 10],
    pestResistance: 'high',
    nativeRegions: [],
    toxicity: { pets: true, children: false },
    windTolerance: 'low',
    isPerennial: false,
    overwinteringNote: 'Annual outdoors. Can overwinter indoors as houseplant.',
    wateringFrequency: 'Every 2–3 days; do not overwater',
    fertilizingFrequency: 'Every 2–3 weeks',
    description: 'The go-to flower for shady spots. Reliable, tidy, and blooms without deadheading all season. Bronze-leafed varieties tolerate more sun. Toxic to pets — keep safe.',
    careLevel: 'easy',
    height: 'low',
    tags: ['shade-loving', 'no-deadheading', 'toxic'],
  },
  {
    id: 'begonia-tuberous',
    name: 'Tuberous Begonia',
    scientificName: 'Begonia × tuberhybrida',
    emoji: '🌹',
    accentColor: '#f9a0b0',
    role: ['filler', 'thriller'],
    hardiness: { min: 9, max: 11 },
    sunlight: ['partial-shade'],
    water: 'moderate',
    minContainerSize: 'medium',
    colors: ['red', 'pink', 'orange', 'yellow', 'white'],
    bloomMonths: [6, 7, 8, 9],
    pestResistance: 'moderate',
    nativeRegions: [],
    toxicity: { pets: true, children: false },
    windTolerance: 'low',
    isPerennial: true,
    overwinteringNote: 'Store tubers indoors in a cool, dry place over winter. Replant in spring.',
    wateringFrequency: 'Every 2–3 days; never let dry completely',
    fertilizingFrequency: 'Every 2 weeks with high-potassium fertilizer',
    description: 'Spectacular rose-like blooms that look almost artificial. Best in partial shade — full sun bleaches the flowers. Dig up tubers to reuse year after year.',
    careLevel: 'moderate',
    height: 'medium',
    tags: ['showy', 'shade-tolerant', 'reusable-tubers'],
  },
  {
    id: 'marigold',
    name: 'Marigold',
    scientificName: 'Tagetes patula / erecta',
    emoji: '🌼',
    accentColor: '#f9c74f',
    role: ['filler'],
    hardiness: { min: 2, max: 13 },
    sunlight: ['full-sun'],
    water: 'low',
    minContainerSize: 'small',
    colors: ['yellow', 'orange', 'red', 'bi-color'],
    bloomMonths: [5, 6, 7, 8, 9, 10],
    pestResistance: 'high',
    nativeRegions: ['SW', 'SE'],
    toxicity: { pets: false, children: false },
    windTolerance: 'high',
    isPerennial: false,
    overwinteringNote: 'Annual. Incredibly easy from seed. Plant new ones each spring.',
    wateringFrequency: 'Every 2–3 days; very drought-tolerant once established',
    fertilizingFrequency: 'Monthly; too much fertilizer reduces blooms',
    description: 'Cheerful, incredibly tough, and a natural pest deterrent. The scent repels aphids, mosquitoes, and even rabbits. Deadhead to keep blooming all season.',
    careLevel: 'easy',
    height: 'low',
    tags: ['pest-deterrent', 'drought-tolerant', 'easy-from-seed'],
  },
  {
    id: 'pansy',
    name: 'Pansy',
    scientificName: 'Viola × wittrockiana',
    emoji: '💜',
    accentColor: '#c3a8e8',
    role: ['filler'],
    hardiness: { min: 4, max: 8 },
    sunlight: ['full-sun', 'partial-shade'],
    water: 'moderate',
    minContainerSize: 'small',
    colors: ['purple', 'yellow', 'orange', 'blue', 'white', 'bi-color'],
    bloomMonths: [3, 4, 5, 9, 10, 11],
    pestResistance: 'moderate',
    nativeRegions: [],
    toxicity: { pets: false, children: false },
    windTolerance: 'moderate',
    isPerennial: false,
    overwinteringNote: 'Cool-season annual. Plant in early spring or fall. Goes dormant in summer heat.',
    wateringFrequency: 'Every 1–2 days in warm weather',
    fertilizingFrequency: 'Every 2 weeks',
    description: 'Happy little faces in almost every color. A cool-season superstar — plant in spring and fall when most other flowers struggle. Perfect for late-season color.',
    careLevel: 'easy',
    height: 'low',
    tags: ['cool-season', 'spring', 'fall', 'cheerful'],
  },
  {
    id: 'zinnia',
    name: 'Zinnia',
    scientificName: 'Zinnia elegans',
    emoji: '🌸',
    accentColor: '#f9a05a',
    role: ['filler', 'thriller'],
    hardiness: { min: 3, max: 13 },
    sunlight: ['full-sun'],
    water: 'low',
    minContainerSize: 'small',
    colors: ['red', 'orange', 'yellow', 'pink', 'white', 'purple', 'bi-color'],
    bloomMonths: [6, 7, 8, 9, 10],
    pestResistance: 'moderate',
    nativeRegions: ['SW', 'SE', 'MW'],
    toxicity: { pets: false, children: false },
    windTolerance: 'high',
    isPerennial: false,
    overwinteringNote: 'Annual. Easy to direct-sow in warm soil. Plant after last frost.',
    wateringFrequency: 'Every 2–3 days; very heat-tolerant',
    fertilizingFrequency: 'Monthly',
    description: 'A butterfly magnet that loves heat. Incredibly easy to grow, vibrant colors, and a pollinator favorite. Cut flowers to encourage more blooms. Avoid wetting the leaves.',
    careLevel: 'easy',
    height: 'medium',
    tags: ['butterfly-friendly', 'heat-tolerant', 'cut-flower'],
  },
  {
    id: 'snapdragon',
    name: 'Snapdragon',
    scientificName: 'Antirrhinum majus',
    emoji: '🌷',
    accentColor: '#f4a0b0',
    role: ['filler', 'thriller'],
    hardiness: { min: 7, max: 11 },
    sunlight: ['full-sun', 'partial-shade'],
    water: 'moderate',
    minContainerSize: 'small',
    colors: ['red', 'pink', 'orange', 'yellow', 'white', 'purple', 'bi-color'],
    bloomMonths: [3, 4, 5, 6, 9, 10, 11],
    pestResistance: 'moderate',
    nativeRegions: [],
    toxicity: { pets: false, children: false },
    windTolerance: 'moderate',
    isPerennial: false,
    overwinteringNote: 'Cool-season annual. Blooms best in spring and fall. May reseed itself.',
    wateringFrequency: 'Every 1–2 days',
    fertilizingFrequency: 'Every 2 weeks',
    description: 'Tall spikes of flowers that kids love to make "snap". Excellent vertical interest for containers. Blooms in cool weather — a perfect spring thriller or filler.',
    careLevel: 'easy',
    height: 'medium',
    tags: ['cool-season', 'vertical', 'kids-friendly'],
  },
  {
    id: 'impatiens',
    name: 'Impatiens',
    scientificName: 'Impatiens walleriana',
    emoji: '🌸',
    accentColor: '#f4b8c8',
    role: ['filler'],
    hardiness: { min: 10, max: 13 },
    sunlight: ['partial-shade', 'full-shade'],
    water: 'high',
    minContainerSize: 'small',
    colors: ['pink', 'red', 'white', 'orange', 'purple', 'coral'],
    bloomMonths: [5, 6, 7, 8, 9, 10],
    pestResistance: 'low',
    nativeRegions: [],
    toxicity: { pets: false, children: false },
    windTolerance: 'low',
    isPerennial: false,
    overwinteringNote: 'Annual. Wilts quickly without water — choose a shadier spot.',
    wateringFrequency: 'Daily in warm weather; very thirsty',
    fertilizingFrequency: 'Every 2 weeks',
    description: 'The king of shade flowers. Non-stop blooms in deep shade where almost nothing else performs. Needs regular water. New Guinea impatiens are more heat/sun tolerant.',
    careLevel: 'easy',
    height: 'low',
    tags: ['shade-loving', 'no-deadheading', 'bright-colors'],
  },
  {
    id: 'new-guinea-impatiens',
    name: 'New Guinea Impatiens',
    scientificName: 'Impatiens hawkeri',
    emoji: '🌺',
    accentColor: '#f97060',
    role: ['filler'],
    hardiness: { min: 10, max: 13 },
    sunlight: ['full-sun', 'partial-shade'],
    water: 'moderate',
    minContainerSize: 'small',
    colors: ['orange', 'red', 'pink', 'coral', 'white'],
    bloomMonths: [5, 6, 7, 8, 9, 10],
    pestResistance: 'moderate',
    nativeRegions: [],
    toxicity: { pets: false, children: false },
    windTolerance: 'low',
    isPerennial: false,
    overwinteringNote: 'Annual. More sun and heat tolerant than standard impatiens.',
    wateringFrequency: 'Every 1–2 days',
    fertilizingFrequency: 'Every 2 weeks',
    description: 'Larger, more vibrant flowers than standard impatiens with showier foliage. Handles more sun. Excellent for containers on partly shaded balconies.',
    careLevel: 'easy',
    height: 'low',
    tags: ['sun-tolerant', 'vibrant', 'tropical-look'],
  },
  {
    id: 'fuchsia',
    name: 'Fuchsia',
    scientificName: 'Fuchsia × hybrida',
    emoji: '💜',
    accentColor: '#e040fb',
    role: ['filler', 'spiller'],
    hardiness: { min: 10, max: 11 },
    sunlight: ['partial-shade', 'full-shade'],
    water: 'high',
    minContainerSize: 'medium',
    colors: ['pink', 'purple', 'red', 'white', 'bi-color'],
    bloomMonths: [5, 6, 7, 8, 9],
    pestResistance: 'low',
    nativeRegions: [],
    toxicity: { pets: false, children: false },
    windTolerance: 'low',
    isPerennial: true,
    overwinteringNote: 'Perennial in mild climates. Cut back and bring indoors before frost.',
    wateringFrequency: 'Daily; hates drying out',
    fertilizingFrequency: 'Weekly for best blooms',
    description: 'Dangling ballerina-like blooms in vivid pink and purple. A showstopper in hanging baskets and shaded spots. Needs consistent moisture and cool temperatures.',
    careLevel: 'moderate',
    height: 'medium',
    tags: ['shade-loving', 'hummingbird-friendly', 'hanging-basket'],
  },
  {
    id: 'vinca',
    name: 'Vinca / Periwinkle',
    scientificName: 'Catharanthus roseus',
    emoji: '🌸',
    accentColor: '#b088f9',
    role: ['filler'],
    hardiness: { min: 9, max: 13 },
    sunlight: ['full-sun'],
    water: 'low',
    minContainerSize: 'small',
    colors: ['pink', 'white', 'red', 'purple', 'coral'],
    bloomMonths: [5, 6, 7, 8, 9, 10],
    pestResistance: 'high',
    nativeRegions: [],
    toxicity: { pets: true, children: true },
    windTolerance: 'high',
    isPerennial: false,
    overwinteringNote: 'Annual in most climates. Self-cleans — no deadheading needed.',
    wateringFrequency: 'Every 2–3 days; excellent drought tolerance',
    fertilizingFrequency: 'Monthly',
    description: 'Near-bulletproof in heat and humidity. Blooms without deadheading all season. One of the best choices for hot, sunny balconies. Note: toxic to pets and children.',
    careLevel: 'easy',
    height: 'low',
    tags: ['heat-tolerant', 'no-deadheading', 'drought-tolerant', 'toxic'],
  },
  {
    id: 'geranium-upright',
    name: 'Zonal Geranium',
    scientificName: 'Pelargonium × hortorum',
    emoji: '🌺',
    accentColor: '#f97060',
    role: ['filler', 'thriller'],
    hardiness: { min: 10, max: 13 },
    sunlight: ['full-sun', 'partial-shade'],
    water: 'moderate',
    minContainerSize: 'small',
    colors: ['red', 'pink', 'salmon', 'white', 'orange', 'bi-color'],
    bloomMonths: [4, 5, 6, 7, 8, 9, 10],
    pestResistance: 'high',
    nativeRegions: [],
    toxicity: { pets: true, children: false },
    windTolerance: 'high',
    isPerennial: true,
    overwinteringNote: 'Perennial in zones 10+. Bring indoors before frost; great houseplant.',
    wateringFrequency: 'Every 2–3 days; let top inch dry between waterings',
    fertilizingFrequency: 'Every 2 weeks',
    description: 'A classic for a reason — reliable, colorful, drought-tolerant, and nearly pest-free. The zonal (dark ring) foliage is pretty even when not blooming. Toxic to pets.',
    careLevel: 'easy',
    height: 'medium',
    tags: ['classic', 'drought-tolerant', 'long-season'],
  },

  // ─── THRILLERS ────────────────────────────────────────────────────────────
  {
    id: 'canna-lily',
    name: 'Canna Lily',
    scientificName: 'Canna × generalis',
    emoji: '🌿',
    accentColor: '#f9a05a',
    role: ['thriller'],
    hardiness: { min: 7, max: 13 },
    sunlight: ['full-sun'],
    water: 'moderate',
    minContainerSize: 'large',
    colors: ['red', 'orange', 'yellow', 'pink'],
    bloomMonths: [6, 7, 8, 9, 10],
    pestResistance: 'moderate',
    nativeRegions: ['SE', 'SW'],
    toxicity: { pets: false, children: false },
    windTolerance: 'moderate',
    isPerennial: true,
    overwinteringNote: 'Perennial in zones 7+. Dig rhizomes before frost in colder zones; store in cool, dry place.',
    wateringFrequency: 'Every 1–2 days; loves moisture',
    fertilizingFrequency: 'Every 2 weeks with high-phosphorus fertilizer',
    description: 'Bold tropical drama with huge paddle-shaped leaves and vivid flower spikes. Makes any container look instantly lush and exotic. Grows 3–5 ft tall — a true showstopper.',
    careLevel: 'easy',
    height: 'tall',
    tags: ['tropical', 'bold', 'large-container'],
  },
  {
    id: 'elephant-ear',
    name: 'Elephant Ear',
    scientificName: 'Colocasia esculenta',
    emoji: '🍃',
    accentColor: '#2d8a4e',
    role: ['thriller'],
    hardiness: { min: 8, max: 13 },
    sunlight: ['full-sun', 'partial-shade'],
    water: 'high',
    minContainerSize: 'large',
    colors: ['green', 'black', 'purple'],
    bloomMonths: [],
    pestResistance: 'high',
    nativeRegions: [],
    toxicity: { pets: true, children: true },
    windTolerance: 'low',
    isPerennial: true,
    overwinteringNote: 'Perennial in zones 8+. Store bulbs indoors in cool, dry conditions in colder zones.',
    wateringFrequency: 'Daily; loves moisture',
    fertilizingFrequency: 'Every 2 weeks',
    description: 'Massive tropical leaves that create instant drama. Can grow 4+ ft in a single season. The bold foliage is the star — no flowers needed. Toxic; shelter from wind.',
    careLevel: 'easy',
    height: 'tall',
    tags: ['tropical', 'bold-foliage', 'large-container', 'toxic'],
  },
  {
    id: 'cordyline',
    name: 'Cordyline / Ti Plant',
    scientificName: 'Cordyline fruticosa',
    emoji: '🌿',
    accentColor: '#8b1a4a',
    role: ['thriller'],
    hardiness: { min: 9, max: 13 },
    sunlight: ['full-sun', 'partial-shade'],
    water: 'moderate',
    minContainerSize: 'medium',
    colors: ['red', 'purple', 'green', 'bronze', 'pink'],
    bloomMonths: [],
    pestResistance: 'high',
    nativeRegions: [],
    toxicity: { pets: true, children: false },
    windTolerance: 'moderate',
    isPerennial: true,
    overwinteringNote: 'Bring indoors before frost. Makes an excellent houseplant in winter.',
    wateringFrequency: 'Every 2–3 days',
    fertilizingFrequency: 'Monthly',
    description: 'Spiky architectural foliage in stunning jewel tones — burgundy, hot pink, bronze. Provides year-round vertical structure. A favorite thriller in professional arrangements.',
    careLevel: 'easy',
    height: 'tall',
    tags: ['architectural', 'year-round', 'spiky-foliage'],
  },
  {
    id: 'ornamental-grass',
    name: 'Purple Fountain Grass',
    scientificName: 'Pennisetum setaceum "Rubrum"',
    emoji: '🌾',
    accentColor: '#8b4a8a',
    role: ['thriller'],
    hardiness: { min: 9, max: 13 },
    sunlight: ['full-sun'],
    water: 'low',
    minContainerSize: 'medium',
    colors: ['purple', 'burgundy'],
    bloomMonths: [7, 8, 9, 10],
    pestResistance: 'high',
    nativeRegions: [],
    toxicity: { pets: false, children: false },
    windTolerance: 'high',
    isPerennial: true,
    overwinteringNote: 'Perennial in zones 9+. Annual in colder zones. Feathery plumes are stunning in fall.',
    wateringFrequency: 'Every 2–3 days; drought-tolerant once established',
    fertilizingFrequency: 'Monthly',
    description: 'Graceful arching purple foliage topped with fuzzy burgundy plumes in late summer. Adds movement in the breeze. One of the most dramatic thrillers for containers.',
    careLevel: 'easy',
    height: 'tall',
    tags: ['ornamental-grass', 'movement', 'drought-tolerant'],
  },
  {
    id: 'salvia-tall',
    name: 'Salvia / Sage',
    scientificName: 'Salvia splendens / guaranitica',
    emoji: '🌿',
    accentColor: '#5050e8',
    role: ['thriller', 'filler'],
    hardiness: { min: 8, max: 13 },
    sunlight: ['full-sun'],
    water: 'low',
    minContainerSize: 'medium',
    colors: ['blue', 'purple', 'red', 'pink', 'white'],
    bloomMonths: [5, 6, 7, 8, 9, 10],
    pestResistance: 'high',
    nativeRegions: ['SW', 'SE', 'NW'],
    toxicity: { pets: false, children: false },
    windTolerance: 'high',
    isPerennial: true,
    overwinteringNote: 'Perennial in zones 8+. Hummingbirds and bees love it.',
    wateringFrequency: 'Every 2–3 days; very drought-tolerant',
    fertilizingFrequency: 'Monthly',
    description: 'Stunning vertical spikes in true blue or vivid red. An incredible pollinator plant — hummingbirds cannot resist it. Heat tolerant and long-blooming.',
    careLevel: 'easy',
    height: 'tall',
    tags: ['pollinator', 'hummingbird-friendly', 'drought-tolerant', 'native'],
  },
  {
    id: 'angelonia',
    name: 'Angelonia / Summer Snapdragon',
    scientificName: 'Angelonia angustifolia',
    emoji: '💜',
    accentColor: '#a060e0',
    role: ['thriller', 'filler'],
    hardiness: { min: 9, max: 13 },
    sunlight: ['full-sun'],
    water: 'low',
    minContainerSize: 'small',
    colors: ['purple', 'pink', 'white', 'bi-color'],
    bloomMonths: [5, 6, 7, 8, 9, 10],
    pestResistance: 'high',
    nativeRegions: [],
    toxicity: { pets: false, children: false },
    windTolerance: 'high',
    isPerennial: false,
    overwinteringNote: 'Annual in most zones. No deadheading needed.',
    wateringFrequency: 'Every 2–3 days; very heat and drought tolerant',
    fertilizingFrequency: 'Monthly',
    description: 'Elegant flower spikes that bloom without stopping all summer — no deadheading needed. Smells lightly of grapes. Thrives in heat where other flowers give up.',
    careLevel: 'easy',
    height: 'medium',
    tags: ['heat-tolerant', 'no-deadheading', 'fragrant'],
  },
  {
    id: 'lantana',
    name: 'Lantana',
    scientificName: 'Lantana camara',
    emoji: '🌼',
    accentColor: '#f9a05a',
    role: ['thriller', 'filler'],
    hardiness: { min: 8, max: 13 },
    sunlight: ['full-sun'],
    water: 'low',
    minContainerSize: 'medium',
    colors: ['yellow', 'orange', 'red', 'pink', 'purple', 'multi'],
    bloomMonths: [5, 6, 7, 8, 9, 10],
    pestResistance: 'high',
    nativeRegions: ['SE', 'SW'],
    toxicity: { pets: true, children: true },
    windTolerance: 'high',
    isPerennial: true,
    overwinteringNote: 'Perennial in zones 8+. Annual elsewhere. Butterflies adore it.',
    wateringFrequency: 'Every 2–3 days; exceptional drought tolerance',
    fertilizingFrequency: 'Monthly; avoid high-nitrogen (reduces blooms)',
    description: 'A butterfly magnet in blazing heat. Clusters of tiny flowers change color as they age — one plant shows multiple colors at once. Nearly indestructible in hot weather.',
    careLevel: 'easy',
    height: 'medium',
    tags: ['butterfly-friendly', 'drought-tolerant', 'heat-tolerant', 'toxic'],
  },
  {
    id: 'agapanthus',
    name: 'Agapanthus / Lily of the Nile',
    scientificName: 'Agapanthus africanus',
    emoji: '💙',
    accentColor: '#4080f0',
    role: ['thriller'],
    hardiness: { min: 8, max: 11 },
    sunlight: ['full-sun', 'partial-shade'],
    water: 'moderate',
    minContainerSize: 'large',
    colors: ['blue', 'purple', 'white'],
    bloomMonths: [6, 7, 8],
    pestResistance: 'high',
    nativeRegions: [],
    toxicity: { pets: true, children: true },
    windTolerance: 'moderate',
    isPerennial: true,
    overwinteringNote: 'Bring indoors in zones below 8. Blooms better when slightly root-bound.',
    wateringFrequency: 'Every 2–3 days',
    fertilizingFrequency: 'Every 2 weeks during growing season',
    description: 'Dramatic blue globe flowers on tall stems above strap-like foliage. A Mediterranean classic. Interestingly, blooms better when pot-bound — perfect for containers.',
    careLevel: 'easy',
    height: 'tall',
    tags: ['Mediterranean', 'bold-blooms', 'root-bound-ok'],
  },

  // ─── DUAL-ROLE / NATIVE & PERENNIAL STARS ────────────────────────────────
  {
    id: 'lavender',
    name: 'Lavender',
    scientificName: 'Lavandula angustifolia',
    emoji: '💜',
    accentColor: '#c3a8e8',
    role: ['filler', 'thriller'],
    hardiness: { min: 5, max: 9 },
    sunlight: ['full-sun'],
    water: 'low',
    minContainerSize: 'medium',
    colors: ['purple', 'lavender'],
    bloomMonths: [6, 7, 8],
    pestResistance: 'high',
    nativeRegions: [],
    toxicity: { pets: false, children: false },
    windTolerance: 'high',
    isPerennial: true,
    overwinteringNote: 'Perennial in zones 5–9. Excellent drainage is critical — roots rot in wet soil.',
    wateringFrequency: 'Every 5–7 days; extremely drought-tolerant',
    fertilizingFrequency: 'Once in spring only',
    description: 'Fragrant silver-green mounds crowned with purple spikes. Bees adore it. Drought-tolerant once established. Must have excellent drainage — the #1 way people kill lavender is overwatering.',
    careLevel: 'moderate',
    height: 'medium',
    tags: ['fragrant', 'pollinator', 'drought-tolerant', 'deer-resistant'],
  },
  {
    id: 'heuchera',
    name: 'Coral Bells',
    scientificName: 'Heuchera spp.',
    emoji: '🍂',
    accentColor: '#c04040',
    role: ['filler'],
    hardiness: { min: 4, max: 9 },
    sunlight: ['partial-shade', 'full-shade'],
    water: 'moderate',
    minContainerSize: 'small',
    colors: ['burgundy', 'caramel', 'lime', 'purple', 'silver', 'peach'],
    bloomMonths: [5, 6, 7],
    pestResistance: 'high',
    nativeRegions: ['NE', 'MW', 'SE', 'NW', 'SW'],
    toxicity: { pets: false, children: false },
    windTolerance: 'moderate',
    isPerennial: true,
    overwinteringNote: 'Hardy perennial in zones 4–9. Evergreen in mild climates. Foliage is the main show.',
    wateringFrequency: 'Every 2–3 days',
    fertilizingFrequency: 'Monthly in growing season',
    description: 'Stunning foliage plant in jewel tones — deep burgundy, bright lime, silvery purple. Tiny bell-shaped flowers on wiry stems in summer. One of the best natives for shade containers.',
    careLevel: 'easy',
    height: 'low',
    tags: ['native', 'foliage', 'shade-tolerant', 'perennial'],
  },
  {
    id: 'black-eyed-susan',
    name: 'Black-Eyed Susan',
    scientificName: 'Rudbeckia hirta',
    emoji: '🌻',
    accentColor: '#f9c74f',
    role: ['filler', 'thriller'],
    hardiness: { min: 3, max: 9 },
    sunlight: ['full-sun'],
    water: 'low',
    minContainerSize: 'medium',
    colors: ['yellow', 'orange', 'bi-color'],
    bloomMonths: [6, 7, 8, 9, 10],
    pestResistance: 'high',
    nativeRegions: ['NE', 'MW', 'SE', 'SW', 'NW'],
    toxicity: { pets: false, children: false },
    windTolerance: 'high',
    isPerennial: true,
    overwinteringNote: 'Perennial in zones 3–9. Short-lived perennial that self-seeds freely.',
    wateringFrequency: 'Every 2–3 days; very drought-tolerant',
    fertilizingFrequency: 'Once in spring',
    description: 'A native wildflower that lights up containers with golden-yellow daisy blooms all summer. Pollinators love it, deer avoid it, and it thrives on neglect. Truly American.',
    careLevel: 'easy',
    height: 'medium',
    tags: ['native', 'pollinator', 'drought-tolerant', 'wildlife-friendly'],
  },
  {
    id: 'echinacea',
    name: 'Purple Coneflower',
    scientificName: 'Echinacea purpurea',
    emoji: '🌸',
    accentColor: '#d060a0',
    role: ['thriller', 'filler'],
    hardiness: { min: 3, max: 9 },
    sunlight: ['full-sun'],
    water: 'low',
    minContainerSize: 'large',
    colors: ['purple', 'pink', 'orange', 'yellow', 'white'],
    bloomMonths: [6, 7, 8, 9],
    pestResistance: 'high',
    nativeRegions: ['NE', 'MW', 'SE', 'SW'],
    toxicity: { pets: false, children: false },
    windTolerance: 'high',
    isPerennial: true,
    overwinteringNote: 'Long-lived perennial. Leave seed heads over winter — birds eat the seeds.',
    wateringFrequency: 'Every 3–4 days once established; very drought-tolerant',
    fertilizingFrequency: 'Once in spring',
    description: 'An iconic North American native wildflower. Butterflies and bees mob it in bloom. Extremely tough once established. Seed heads feed birds in winter. A true do-gooder plant.',
    careLevel: 'easy',
    height: 'tall',
    tags: ['native', 'pollinator', 'drought-tolerant', 'wildlife-friendly', 'wildlife-seeds'],
  },
  {
    id: 'catmint',
    name: 'Catmint',
    scientificName: 'Nepeta × faassenii',
    emoji: '💜',
    accentColor: '#9080c8',
    role: ['filler', 'spiller'],
    hardiness: { min: 4, max: 8 },
    sunlight: ['full-sun', 'partial-shade'],
    water: 'low',
    minContainerSize: 'medium',
    colors: ['lavender', 'blue-purple'],
    bloomMonths: [5, 6, 7, 8, 9],
    pestResistance: 'high',
    nativeRegions: [],
    toxicity: { pets: false, children: false },
    windTolerance: 'high',
    isPerennial: true,
    overwinteringNote: 'Hardy perennial. Cut back by half after first bloom for a second flush.',
    wateringFrequency: 'Every 3–5 days; very drought-tolerant',
    fertilizingFrequency: 'Once in spring',
    description: 'Soft mounds of silver-green foliage smothered in lavender-blue flowers for months. Shear after first bloom for a fresh flush. Deer and drought resistant, cats adore it.',
    careLevel: 'easy',
    height: 'low',
    tags: ['drought-tolerant', 'deer-resistant', 'pollinator', 'fragrant'],
  },
  {
    id: 'verbena',
    name: 'Verbena',
    scientificName: 'Verbena × hybrida',
    emoji: '🌸',
    accentColor: '#e060c0',
    role: ['filler', 'spiller'],
    hardiness: { min: 9, max: 11 },
    sunlight: ['full-sun'],
    water: 'low',
    minContainerSize: 'small',
    colors: ['red', 'pink', 'purple', 'white', 'bi-color'],
    bloomMonths: [4, 5, 6, 7, 8, 9, 10],
    pestResistance: 'moderate',
    nativeRegions: [],
    toxicity: { pets: false, children: false },
    windTolerance: 'high',
    isPerennial: false,
    overwinteringNote: 'Annual in most zones. Extremely heat and drought tolerant.',
    wateringFrequency: 'Every 2–3 days',
    fertilizingFrequency: 'Every 2–3 weeks',
    description: 'Flat-topped flower clusters in vivid colors that butterflies love. Cascades gently over pot edges. One of the best performers in hot, sunny spots.',
    careLevel: 'easy',
    height: 'low',
    tags: ['butterfly-friendly', 'heat-tolerant', 'drought-tolerant'],
  },
  {
    id: 'osteospermum',
    name: 'African Daisy',
    scientificName: 'Osteospermum spp.',
    emoji: '🌼',
    accentColor: '#f0b870',
    role: ['filler'],
    hardiness: { min: 9, max: 11 },
    sunlight: ['full-sun'],
    water: 'low',
    minContainerSize: 'small',
    colors: ['yellow', 'orange', 'pink', 'purple', 'white', 'bi-color'],
    bloomMonths: [3, 4, 5, 6, 9, 10, 11],
    pestResistance: 'high',
    nativeRegions: [],
    toxicity: { pets: false, children: false },
    windTolerance: 'moderate',
    isPerennial: false,
    overwinteringNote: 'Cool-season annual. Blooms best in spring and fall; slows in summer heat.',
    wateringFrequency: 'Every 2–3 days',
    fertilizingFrequency: 'Every 2–3 weeks',
    description: 'Cheerful daisy blooms with a spoon-shaped center petal giving them a unique look. A spring and fall performer that dazzles when most containers look sparse.',
    careLevel: 'easy',
    height: 'low',
    tags: ['cool-season', 'spring', 'fall', 'daisy-style'],
  },
  {
    id: 'scaevola',
    name: 'Fan Flower',
    scientificName: 'Scaevola aemula',
    emoji: '💙',
    accentColor: '#6080e8',
    role: ['filler', 'spiller'],
    hardiness: { min: 9, max: 11 },
    sunlight: ['full-sun'],
    water: 'moderate',
    minContainerSize: 'small',
    colors: ['blue', 'purple', 'white', 'pink'],
    bloomMonths: [5, 6, 7, 8, 9, 10],
    pestResistance: 'high',
    nativeRegions: [],
    toxicity: { pets: false, children: false },
    windTolerance: 'high',
    isPerennial: false,
    overwinteringNote: 'Annual. Heat and humidity tolerant — unusual for blue-flowering plants.',
    wateringFrequency: 'Every 1–2 days',
    fertilizingFrequency: 'Every 2 weeks',
    description: 'Unusual half-moon shaped fan flowers in true blue or purple. Heat and humidity tolerant — rare for blue flowers. Self-cleaning and a long-season performer.',
    careLevel: 'easy',
    height: 'low',
    tags: ['blue-flowers', 'heat-tolerant', 'humidity-tolerant'],
  },
  {
    id: 'dianthus',
    name: 'Dianthus / Pinks',
    scientificName: 'Dianthus spp.',
    emoji: '🌸',
    accentColor: '#f060a0',
    role: ['filler'],
    hardiness: { min: 4, max: 9 },
    sunlight: ['full-sun'],
    water: 'moderate',
    minContainerSize: 'small',
    colors: ['pink', 'red', 'white', 'bi-color'],
    bloomMonths: [4, 5, 6, 7, 9, 10],
    pestResistance: 'high',
    nativeRegions: [],
    toxicity: { pets: true, children: false },
    windTolerance: 'moderate',
    isPerennial: true,
    overwinteringNote: 'Many varieties are perennial in zones 4–9. Clove-scented blooms.',
    wateringFrequency: 'Every 2–3 days',
    fertilizingFrequency: 'Monthly',
    description: 'Clove-scented fringed flowers with a cottage garden charm. Compact, tidy habit. Many are perennial and bloom twice in spring and fall. Lovely fragrance near seating areas.',
    careLevel: 'easy',
    height: 'low',
    tags: ['fragrant', 'perennial', 'cottage-style', 'deer-resistant'],
  },
  {
    id: 'nemesia',
    name: 'Nemesia',
    scientificName: 'Nemesia spp.',
    emoji: '🌼',
    accentColor: '#f0c060',
    role: ['filler'],
    hardiness: { min: 9, max: 11 },
    sunlight: ['full-sun', 'partial-shade'],
    water: 'moderate',
    minContainerSize: 'small',
    colors: ['yellow', 'orange', 'pink', 'bi-color', 'white'],
    bloomMonths: [3, 4, 5, 9, 10, 11],
    pestResistance: 'high',
    nativeRegions: [],
    toxicity: { pets: false, children: false },
    windTolerance: 'low',
    isPerennial: false,
    overwinteringNote: 'Cool-season annual. Best in spring and fall; lightly fragrant.',
    wateringFrequency: 'Every 1–2 days',
    fertilizingFrequency: 'Every 2 weeks',
    description: 'Cheerful bi-colored blooms with a honey scent. A cool-season gem that blooms when containers can look dull. Compact and tidy — perfect for small pots.',
    careLevel: 'easy',
    height: 'low',
    tags: ['cool-season', 'fragrant', 'spring', 'fall'],
  },
  {
    id: 'celosia',
    name: 'Celosia / Cockscomb',
    scientificName: 'Celosia argentea',
    emoji: '🔥',
    accentColor: '#f04040',
    role: ['thriller', 'filler'],
    hardiness: { min: 9, max: 13 },
    sunlight: ['full-sun'],
    water: 'moderate',
    minContainerSize: 'small',
    colors: ['red', 'orange', 'yellow', 'pink', 'purple'],
    bloomMonths: [6, 7, 8, 9, 10],
    pestResistance: 'high',
    nativeRegions: [],
    toxicity: { pets: false, children: false },
    windTolerance: 'moderate',
    isPerennial: false,
    overwinteringNote: 'Annual. Dried plumes look great in arrangements.',
    wateringFrequency: 'Every 2–3 days',
    fertilizingFrequency: 'Every 2 weeks',
    description: 'Wild, velvety plumes or crests in flame colors. Looks almost fake — in the best way. Loves heat, holds up in humidity, and dries beautifully for indoor arrangements.',
    careLevel: 'easy',
    height: 'medium',
    tags: ['heat-tolerant', 'textural', 'dried-arrangement'],
  },
  {
    id: 'gaura',
    name: 'Gaura / Whirling Butterflies',
    scientificName: 'Oenothera lindheimeri',
    emoji: '🦋',
    accentColor: '#f8c8e8',
    role: ['thriller', 'filler'],
    hardiness: { min: 5, max: 9 },
    sunlight: ['full-sun'],
    water: 'low',
    minContainerSize: 'medium',
    colors: ['white', 'pink'],
    bloomMonths: [5, 6, 7, 8, 9, 10],
    pestResistance: 'high',
    nativeRegions: ['SW', 'SE', 'MW'],
    toxicity: { pets: false, children: false },
    windTolerance: 'high',
    isPerennial: true,
    overwinteringNote: 'Perennial in zones 5–9. Cut to the ground in late fall.',
    wateringFrequency: 'Every 3–5 days; excellent drought tolerance',
    fertilizingFrequency: 'Once in spring',
    description: 'Wispy stems carry delicate butterfly-like flowers that dance in the breeze. Blooms for months without deadheading. A native that brings airy, graceful movement to containers.',
    careLevel: 'easy',
    height: 'tall',
    tags: ['native', 'movement', 'drought-tolerant', 'long-blooming'],
  },
  {
    id: 'dusty-miller',
    name: 'Dusty Miller',
    scientificName: 'Senecio cineraria',
    emoji: '🩶',
    accentColor: '#c0d0d8',
    role: ['filler'],
    hardiness: { min: 8, max: 13 },
    sunlight: ['full-sun', 'partial-shade'],
    water: 'low',
    minContainerSize: 'small',
    colors: ['silver'],
    bloomMonths: [],
    pestResistance: 'high',
    nativeRegions: [],
    toxicity: { pets: true, children: false },
    windTolerance: 'high',
    isPerennial: true,
    overwinteringNote: 'Perennial in zones 8+. Foliage is most vibrant in cooler months.',
    wateringFrequency: 'Every 3–4 days; very drought-tolerant',
    fertilizingFrequency: 'Monthly',
    description: 'Silvery, felt-like foliage that makes every neighboring color pop. A neutral peacemaker in mixed containers. No flowers needed — the texture alone earns its place.',
    careLevel: 'easy',
    height: 'low',
    tags: ['foliage', 'silver', 'drought-tolerant', 'contrast'],
  },
  {
    id: 'coleus',
    name: 'Coleus',
    scientificName: 'Plectranthus scutellarioides',
    emoji: '🍁',
    accentColor: '#e04040',
    role: ['filler', 'thriller'],
    hardiness: { min: 10, max: 13 },
    sunlight: ['partial-shade', 'full-shade'],
    water: 'moderate',
    minContainerSize: 'small',
    colors: ['red', 'burgundy', 'lime', 'orange', 'pink', 'bi-color'],
    bloomMonths: [],
    pestResistance: 'moderate',
    nativeRegions: [],
    toxicity: { pets: true, children: false },
    windTolerance: 'low',
    isPerennial: false,
    overwinteringNote: 'Annual outdoors. Overwinters beautifully as a houseplant. Pinch flowers off to keep foliage vibrant.',
    wateringFrequency: 'Every 1–2 days; keep moist',
    fertilizingFrequency: 'Every 2 weeks',
    description: 'Kaleidoscopic foliage in every color combination imaginable. The shade garden\'s answer to colorful annuals. Pinch off any flower spikes to keep the bold leaf color.',
    careLevel: 'easy',
    height: 'medium',
    tags: ['shade-loving', 'foliage', 'colorful', 'pinch-flowers'],
  },
  {
    id: 'torenia',
    name: 'Wishbone Flower',
    scientificName: 'Torenia fournieri',
    emoji: '💜',
    accentColor: '#8060d0',
    role: ['filler'],
    hardiness: { min: 10, max: 13 },
    sunlight: ['partial-shade'],
    water: 'moderate',
    minContainerSize: 'small',
    colors: ['purple', 'pink', 'white', 'bi-color'],
    bloomMonths: [6, 7, 8, 9, 10],
    pestResistance: 'high',
    nativeRegions: [],
    toxicity: { pets: false, children: false },
    windTolerance: 'low',
    isPerennial: false,
    overwinteringNote: 'Annual. Excellent in humid climates and shady spots.',
    wateringFrequency: 'Every 1–2 days; keep moist',
    fertilizingFrequency: 'Every 2 weeks',
    description: 'Charming tubular flowers that look like tiny snapdragons. One of the best shade annuals for summer color. Thrives where impatiens struggles in heat.',
    careLevel: 'easy',
    height: 'low',
    tags: ['shade-loving', 'summer-color', 'humidity-tolerant'],
  },
]

export function getPlantById(id: string): Plant | undefined {
  return plants.find(p => p.id === id)
}

export function filterPlants(
  hardinessZone: number | null,
  filters: {
    roles?: string[]
    sunlight?: string[]
    water?: string[]
    colors?: string[]
    pestResistance?: string[]
    onlyNative?: boolean
    onlySafePets?: boolean
    onlySafeChildren?: boolean
    onlyPerennial?: boolean
    windTolerance?: string[]
    minContainerSize?: string
    searchText?: string
  }
): Plant[] {
  return plants.filter(plant => {
    if (hardinessZone !== null) {
      if (plant.hardiness.min > hardinessZone || plant.hardiness.max < hardinessZone) return false
    }
    if (filters.roles?.length && !filters.roles.some(r => plant.role.includes(r as never))) return false
    if (filters.sunlight?.length && !filters.sunlight.some(s => plant.sunlight.includes(s as never))) return false
    if (filters.water?.length && !filters.water.includes(plant.water)) return false
    if (filters.colors?.length && !filters.colors.some(c => plant.colors.includes(c))) return false
    if (filters.pestResistance?.length && !filters.pestResistance.includes(plant.pestResistance)) return false
    if (filters.onlyNative && plant.nativeRegions.length === 0) return false
    if (filters.onlySafePets && plant.toxicity.pets) return false
    if (filters.onlySafeChildren && plant.toxicity.children) return false
    if (filters.onlyPerennial && !plant.isPerennial) return false
    if (filters.windTolerance?.length && !filters.windTolerance.includes(plant.windTolerance)) return false
    if (filters.minContainerSize) {
      const sizeOrder = ['small', 'medium', 'large']
      if (sizeOrder.indexOf(plant.minContainerSize) > sizeOrder.indexOf(filters.minContainerSize)) return false
    }
    if (filters.searchText) {
      const q = filters.searchText.toLowerCase()
      if (!plant.name.toLowerCase().includes(q) && !plant.description.toLowerCase().includes(q)) return false
    }
    return true
  })
}
